import type {
  AssistantModalHandle,
  TellentAssistantMobileModalPresenter,
} from '@core/tellent-assistant-mobile-contracts';
import { TELLENT_ASSISTANT_MOBILE_SHEET_EVENT } from '@core/tellent-assistant-mobile-contracts';

/**
 * The mobile assistant sheet's chrome is a React-owned `IonModal` in the host app (manage), not
 * an Angular/Ionic-Angular modal — see the tellent-assistant-hr-mobile wiring plan. This presenter
 * fulfils `TellentAssistantMobileService`'s imperative present()/dismiss() contract without
 * rendering anything itself: it just tells the host, via plain `window` CustomEvents, when the
 * sheet should be shown/hidden, and listens for the host's own dismissal (backdrop, swipe,
 * hardware back) to resolve `onWillDismiss()`. `TellentAssistantMobileSheet.tsx` is the React
 * counterpart.
 */
export function createTellentAssistantMobileSheetPresenter(): TellentAssistantMobileModalPresenter {
  return {
    createCard: (): Promise<AssistantModalHandle> => {
      let resolveWillDismiss: (value: unknown) => void = () => undefined;
      const willDismiss = new Promise<unknown>(resolve => {
        resolveWillDismiss = resolve;
      });
      const onHostClosed = () => resolveWillDismiss(undefined);
      window.addEventListener(TELLENT_ASSISTANT_MOBILE_SHEET_EVENT.close, onHostClosed, {
        once: true,
      });

      const handle: AssistantModalHandle = {
        present: () => {
          window.dispatchEvent(new CustomEvent(TELLENT_ASSISTANT_MOBILE_SHEET_EVENT.open));
          return Promise.resolve();
        },
        // Dispatching is enough — `onHostClosed` above is still armed (unless this handle already
        // dismissed once) and resolves `willDismiss` itself the moment this fires, self-removing
        // via `{ once: true }`.
        dismiss: () => {
          window.dispatchEvent(new CustomEvent(TELLENT_ASSISTANT_MOBILE_SHEET_EVENT.close));
          return Promise.resolve(true);
        },
        onWillDismiss: () => willDismiss,
      };
      return Promise.resolve(handle);
    },
  };
}
